import React from 'react';
import posthog from 'posthog-js';
import { useAuth } from '../contexts/AuthContext';
import ReferralDashboard from './ReferralDashboard';
import ActivityGrid from './ActivityGrid';
import PnLDashboard from './PnLDashboard';
import HistoryTable from './HistoryTable';
import './Profile.css';

interface ProfileProps {
  onBack: () => void;
}

const Profile: React.FC<ProfileProps> = ({ onBack }) => {
  const { user } = useAuth();
  const [selectedView, setSelectedView] = React.useState<'Referrals' | 'Activity' | 'PnL' | 'History'>('Referrals');

  const formatCurrency = (value: string | undefined) => {
    if (!value) return '$0.00';
    // Remove any existing dollar sign and parse the number
    const numericValue = parseFloat(value.replace('$', ''));
    if (isNaN(numericValue)) return value; // Return original if not a valid number

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(numericValue);
  };

  return (
    <div className="profile-page">
      <div className="profile-header">
        <button className="back-button" onClick={onBack}>← Back</button>
      </div>

      <div className="profile-content">
        {/* User Info Section */}
        <div className="user-info-section">
          <div className="user-info-card">
            <div className="user-avatar-large">
              {user?.email?.charAt(0).toUpperCase()}
            </div>
            <div className="user-details">
              <div className="user-email">{user?.email}</div>
              <div className="user-wallet">
                {user?.walletAddress ? 
                  `${user.walletAddress.slice(0, 6)}...${user.walletAddress.slice(-4)}` : 
                  '...'
                }
              </div>
            </div>
            <div className="user-balances">
              <div className="balance-item">
                <span className="balance-label">Portfolio</span>
                <span className="balance-value">{formatCurrency(user?.portfolioBalance)}</span>
              </div>
              <div className="balance-item">
                <span className="balance-label">Cash</span>
                <span className="balance-value">{formatCurrency(user?.cashBalance)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Filter Toggle */}
        <div className="profile-view-toggle" role="tablist" aria-label="Profile view filter">
          <button
            className={`profile-view-btn ${selectedView === 'Referrals' ? 'active' : ''}`}
            onClick={() => { setSelectedView('Referrals'); try { posthog.capture('engagement:profile_tab', { tab: 'Referrals' }); } catch {} }}
            aria-pressed={selectedView === 'Referrals'}
          >
            Referrals
          </button>
          <button
            className={`profile-view-btn ${selectedView === 'Activity' ? 'active' : ''}`}
            onClick={() => { setSelectedView('Activity'); try { posthog.capture('engagement:profile_tab', { tab: 'Activity' }); } catch {} }}
            aria-pressed={selectedView === 'Activity'}
          >
            Activity
          </button>
          <button
            className={`profile-view-btn ${selectedView === 'PnL' ? 'active' : ''}`}
            onClick={() => { setSelectedView('PnL'); try { posthog.capture('engagement:profile_tab', { tab: 'PnL' }); } catch {} }}
            aria-pressed={selectedView === 'PnL'}
          >
            Profit / Loss
          </button>
          <button
            className={`profile-view-btn ${selectedView === 'History' ? 'active' : ''}`}
            onClick={() => { setSelectedView('History'); try { posthog.capture('engagement:profile_tab', { tab: 'History' }); } catch {} }}
            aria-pressed={selectedView === 'History'}
          >
            History
          </button>
        </div>

        {/* Conditional Content */}
        {selectedView === 'Referrals' ? (
          <ReferralDashboard />
        ) : selectedView === 'Activity' ? (
          <ActivityGrid />
        ) : selectedView === 'PnL' ? (
          <PnLDashboard />
        ) : (
          <HistoryTable />
        )}
      </div>
    </div>
  );
};

export default Profile;

